import { compact } from 'lodash';
import { useMemo } from 'react';

import { BankIcon } from '@/components/icons/BankIcon';
import { BarChartSquare01Icon } from '@/components/icons/BarChartSquare01Icon';
import { CheckCircleBrokenIcon } from '@/components/icons/CheckCircleBrokenIcon';
import { File05Icon } from '@/components/icons/File05Icon';
import { FileCheck02Icon } from '@/components/icons/FileCheck02Icon';
import { FileSearch02Icon } from '@/components/icons/FileSearch02Icon';
import { Gift01Icon } from '@/components/icons/Gift01Icon';
import { GraduationHat02Icon } from '@/components/icons/GraduationHat02Icon';
import { Lightbulb03Icon } from '@/components/icons/Lightbulb03Icon';
import { Map01Icon } from '@/components/icons/Map01Icon';
import { PieChart03Icon } from '@/components/icons/PieChart03Icon';
import { PresentationChart01Icon } from '@/components/icons/PresentationChart01Icon';
import { Scales01Icon } from '@/components/icons/Scales01Icon';
import { TrendUp01Icon } from '@/components/icons/TrendUp01Icon';
import { User01Icon } from '@/components/icons/User01Icon';
import { Wallet01Icon } from '@/components/icons/Wallet01Icon';
import { useFeatureFlag } from '@/modules/featureFlags/useFeatureFlag';
import { useAICapabilitiesEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useAiInsightsEnabled } from '@/modules/tenant/TenantDetailsContext/hooks/useAiInsightsEnabled';
import { useTenantDetailsContext } from '@/modules/tenant/TenantDetailsContext/TenantDetailsContext';
import { ROUTE_KEYS } from '@/navigation/constants';
import { getCompletePathFromRouteKey } from '@/navigation/navigationUtils';
import { useCanAccessPage } from '@/navigation/usePageAccessLookup';

import { MenuItemProps } from './MenuItem';
import { menuItemSxProps } from './sidebarConstants';
interface UseMenuItemsParams {
  householdId: string;
  isExpanded: boolean;
}

export const HOUSEHOLD_SIDEBAR_GROUPS = {
  CLIENT: 'Client',
  TOOLS: 'Tools',
  REPORTING: 'Reporting',
  // The diagram_builder flag uses these Studio MVP labels in place of the
  // Client, Tools, and Reporting labels above.
  OVERVIEW: 'Overview',
  PLANNER: 'Planner',
  STUDIO: 'Studio',
} as const;

export const TASKS_MENU_LABEL = 'Tasks';
export const PRESENTATIONS_MENU_LABEL = 'Presentations';
export const CALCULATOR_MENU_LABEL = 'Calculator';

export function useMenuItems({
  householdId,
  isExpanded,
}: UseMenuItemsParams): MenuItemProps[] {
  const canAccessTasksPage = useCanAccessPage(ROUTE_KEYS.TASKS);
  const canAccessFormsPage = useCanAccessPage(
    ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSIONS
  );
  const { projectionFunctionalityEnabled, hasPublishedReviews } =
    useTenantDetailsContext();
  const aiEnabled = useAICapabilitiesEnabled();
  const aiInsightsEnabled = useAiInsightsEnabled();
  const diagramBuilderEnabled = useFeatureFlag('diagram_builder');

  return useMemo(() => {
    const variant = isExpanded ? 'large' : 'small';
    const clientGroup = diagramBuilderEnabled
      ? HOUSEHOLD_SIDEBAR_GROUPS.OVERVIEW
      : HOUSEHOLD_SIDEBAR_GROUPS.CLIENT;
    const toolsGroup = diagramBuilderEnabled
      ? HOUSEHOLD_SIDEBAR_GROUPS.PLANNER
      : HOUSEHOLD_SIDEBAR_GROUPS.TOOLS;
    const reportingGroup = diagramBuilderEnabled
      ? HOUSEHOLD_SIDEBAR_GROUPS.STUDIO
      : HOUSEHOLD_SIDEBAR_GROUPS.REPORTING;
    // The Studio MVP layout shows Insights after Education.
    const insightsItem = aiEnabled &&
      aiInsightsEnabled && {
        text: 'Insights',
        groupName: toolsGroup,
        icon: <Lightbulb03Icon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_INSIGHTS, {
          householdId,
        }),
      };

    return compact([
      {
        text: diagramBuilderEnabled ? 'Dashboard' : 'Overview',
        groupName: clientGroup,
        icon: <PieChart03Icon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_OVERVIEW, {
          householdId,
        }),
      },
      {
        text: 'Documents',
        groupName: clientGroup,
        icon: <FileSearch02Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_DOCUMENTS,
          {
            householdId,
          }
        ),
      },
      {
        text: 'People',
        groupName: clientGroup,
        icon: <User01Icon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_SIDEBAR_PEOPLE, {
          householdId,
        }),
      },
      {
        text: 'Estate',
        groupName: clientGroup,
        icon: <BankIcon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_SIDEBAR_ESTATE, {
          householdId,
        }),
      },
      {
        text: 'Cash flows',
        groupName: clientGroup,
        icon: <Wallet01Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_SIDEBAR_CASHFLOWS,
          {
            householdId,
          }
        ),
      },
      {
        text: 'Exemptions',
        groupName: clientGroup,
        icon: <Gift01Icon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_SIDEBAR_GIFTING, {
          householdId,
        }),
      },
      projectionFunctionalityEnabled && {
        text: 'Waterfall',
        groupName: toolsGroup,
        icon: <Map01Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_ESTATE_WATERFALL_TAB,
          {
            householdId,
          }
        ),
      },
      {
        text: 'Ownership',
        groupName: toolsGroup,
        icon: <Scales01Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_SIDEBAR_OWNERSHIP,
          {
            householdId,
          }
        ),
      },
      {
        text: PRESENTATIONS_MENU_LABEL,
        groupName: reportingGroup,
        icon: <PresentationChart01Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PRESENTATIONS,
          {
            householdId,
          }
        ),
      },
      diagramBuilderEnabled && {
        text: 'Artifacts',
        groupName: reportingGroup,
        icon: <File05Icon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_ARTIFACT_BUILDER, {
          householdId,
        }),
      },
      {
        text: CALCULATOR_MENU_LABEL,
        groupName: toolsGroup,
        icon: <BarChartSquare01Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PROPOSALS,
          {
            householdId,
          }
        ),
      },
      canAccessFormsPage &&
        hasPublishedReviews && {
          text: 'Reviews',
          groupName: toolsGroup,
          icon: <FileCheck02Icon size={16} />,
          to: getCompletePathFromRouteKey(
            ROUTE_KEYS.HOUSEHOLD_FORM_SUBMISSIONS,
            {
              householdId,
            }
          ),
        },
      !diagramBuilderEnabled && insightsItem,
      {
        text: 'Education',
        groupName: toolsGroup,
        icon: <GraduationHat02Icon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.EDUCATION, {
          householdId,
        }),
      },
      diagramBuilderEnabled && insightsItem,
      canAccessTasksPage && {
        text: TASKS_MENU_LABEL,
        groupName: toolsGroup,
        icon: <CheckCircleBrokenIcon size={16} />,
        to: getCompletePathFromRouteKey(ROUTE_KEYS.HOUSEHOLD_DETAILS_TASKS, {
          householdId,
        }),
        variant,
        menuItemSxProps,
      },
      projectionFunctionalityEnabled && {
        text: diagramBuilderEnabled ? 'Value Creation' : 'Value creation',
        groupName: reportingGroup,
        icon: <TrendUp01Icon size={16} />,
        to: getCompletePathFromRouteKey(
          ROUTE_KEYS.HOUSEHOLD_DETAILS_PERFORMANCE,
          {
            householdId,
          }
        ),
      },
    ]).map((item) => ({
      ...item,
      variant,
      menuItemSxProps,
    }));
  }, [
    isExpanded,
    householdId,
    projectionFunctionalityEnabled,
    aiEnabled,
    aiInsightsEnabled,
    diagramBuilderEnabled,
    canAccessTasksPage,
    canAccessFormsPage,
    hasPublishedReviews,
  ]);
}
